import { OperatorType, ShopsProductsCategoriesList } from '@/types/generated-types/graphql';
import { UserState } from '@/composables/User';
import { getAuthHeaders } from '@/composables/nestApi';

const fields = ['id_shop_product_category_list', 'id_category', 'name'];
const fieldsName = fields.reduce((acc: string, current: string) => `${acc} ${current}`);

// AbortController pour annuler les requêtes en cours
let currentAbortController: AbortController | null = null;

async function executeGraphQL(
  filters: any[],
  limit: number,
  offset: number,
  signal: AbortSignal,
): Promise<ShopsProductsCategoriesList[]> {
  const query = `query ($limit: Int!, $offset: Int!, $filter: [FilterParameters]) {
    ShopsProductsCategoriesSelection (params: {limit: $limit, offset: $offset, filter: $filter}) {
      items { ${fieldsName} }
    }
  }`;

  const response = await fetch('/api/graphql', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify({
      query,
      variables: { limit, offset, filter: filters },
    }),
    signal,
  });

  const result = await response.json();
  return result.data?.ShopsProductsCategoriesSelection?.items ?? [];
}

async function searchByName(
  name: string,
  lang: string,
  limit: number,
  offset: number,
  signal: AbortSignal,
): Promise<ShopsProductsCategoriesList[]> {
  const filters = [
    { field: 'name', operator: OperatorType.Against, value: name },
    { field: 'id_shop', operator: OperatorType.Equals, value: UserState.activeShop?.id },
    { field: 'lang', operator: OperatorType.Equals, value: lang },
  ];

  return executeGraphQL(filters, limit, offset, signal);
}

async function searchByCategoryId(
  categoryId: string,
  lang: string,
  signal: AbortSignal,
): Promise<ShopsProductsCategoriesList[]> {
  const filters = [
    { field: 'id_category', operator: OperatorType.Equals, value: categoryId },
    { field: 'id_shop', operator: OperatorType.Equals, value: UserState.activeShop?.id },
    { field: 'lang', operator: OperatorType.Equals, value: lang },
  ];

  return executeGraphQL(filters, 1, 0, signal);
}

export default async function ShopsProductsCategoriesListList(name: string, lang: string, limit = 50, offset = 0): Promise<Array<ShopsProductsCategoriesList>|null> {
  // Annuler la requête précédente
  if (currentAbortController) {
    currentAbortController.abort();
  }
  currentAbortController = new AbortController();
  const { signal } = currentAbortController;

  const searchTerm = name.trim();

  // Ne pas lancer la recherche si le terme est vide
  if (!searchTerm) {
    return [];
  }

  const isSingleWord = !searchTerm.includes(' ');

  try {
    // Si un seul mot et première page : recherche par nom ET par id_category en parallèle
    if (isSingleWord && offset === 0) {
      const [nameResults, idResults] = await Promise.all([
        searchByName(searchTerm, lang, limit, offset, signal),
        searchByCategoryId(searchTerm, lang, signal),
      ]);

      // Fusionner : id_category en premier, puis les résultats par nom (sans doublons)
      const mergedResults = [...idResults];
      nameResults.forEach((category) => {
        if (!mergedResults.some((c) => c.id_shop_product_category_list === category.id_shop_product_category_list)) {
          mergedResults.push(category);
        }
      });

      return mergedResults.slice(0, limit);
    }

    // Recherche standard par nom uniquement
    return searchByName(searchTerm, lang, limit, offset, signal);
  } catch (error: any) {
    // Ignorer les erreurs d'abort (requête annulée)
    if (error.name === 'AbortError') {
      return null;
    }
    throw error;
  }
}
